import { useCallback, useRef, useEffect, useState } from 'react';
import { Loader2, ChevronDown } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useLegalDocumentContent } from '@/hooks/useLegalDocumentContent';
import { useLanguage } from '@/contexts/LanguageContext';
import { sanitizeHtml } from '@/utils/sanitizeHtml';

interface LegalDocumentContentProps {
  /** The type of legal document (e.g., 'privacy-policy', 'terms-of-use') */
  documentType: string;
  /** The version of the document to display */
  version: string;
  /** Optional callback when user scrolls to the bottom of the content */
  onScrolledToBottom?: () => void;
  /** Whether to show the scroll indicator (default: true if onScrolledToBottom is provided) */
  showScrollIndicator?: boolean;
}

/**
 * Shared component for displaying legal document content.
 * Used by both AgreementAcceptanceDialog and LegalDocumentViewDialog.
 */
export function LegalDocumentContent({
  documentType,
  version,
  onScrolledToBottom,
  showScrollIndicator,
}: LegalDocumentContentProps) {
  const { t, language } = useLanguage();
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);

  // Fetch agreement content
  const {
    content: agreementContent,
    isLoading: isLoadingContent,
    error: contentError,
  } = useLegalDocumentContent(documentType, version, language, {
    enabled: !!documentType && !!version,
  });

  // Determine if scroll indicator should be shown
  const shouldShowScrollIndicator = showScrollIndicator ?? !!onScrolledToBottom;

  /**
   * Check if user has scrolled to the bottom of the content
   */
  const checkScrollPosition = useCallback(() => {
    const viewport = scrollAreaRef.current?.querySelector('[data-radix-scroll-area-viewport]');
    if (viewport) {
      const { scrollTop, scrollHeight, clientHeight } = viewport;
      // Consider "at bottom" if within 20px of the end
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 20;
      if (isAtBottom && !hasScrolledToBottom) {
        setHasScrolledToBottom(true);
        onScrolledToBottom?.();
      }
    }
  }, [hasScrolledToBottom, onScrolledToBottom]);

  /**
   * Reset scroll state when document content changes
   */
  useEffect(() => {
    setHasScrolledToBottom(false);
  }, [documentType, version, agreementContent]);

  /**
   * Set up scroll listener when content is loaded
   */
  useEffect(() => {
    const viewport = scrollAreaRef.current?.querySelector('[data-radix-scroll-area-viewport]');
    if (viewport && agreementContent) {
      // Check initial position (content might be short enough to not need scrolling)
      const { scrollHeight, clientHeight } = viewport;
      if (scrollHeight <= clientHeight) {
        setHasScrolledToBottom(true);
        onScrolledToBottom?.();
      }

      viewport.addEventListener('scroll', checkScrollPosition);
      return () => { viewport.removeEventListener('scroll', checkScrollPosition); };
    }
  }, [agreementContent, checkScrollPosition, onScrolledToBottom]);

  if (isLoadingContent) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (contentError) {
    return (
      <div className="py-8 text-center text-destructive">
        {t.dialogs.agreementAcceptance.loadError}
      </div>
    );
  }

  return (
    <div className="relative">
      <ScrollArea ref={scrollAreaRef} className="h-[400px] rounded-md border p-4">
        <div
          className="prose prose-sm dark:prose-invert max-w-none"
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(agreementContent ?? '') }}
        />
      </ScrollArea>
      {shouldShowScrollIndicator && !hasScrolledToBottom && (
        <div className="absolute bottom-0 left-0 right-0 flex flex-col items-center pb-2 pt-8 bg-gradient-to-t from-background to-transparent pointer-events-none">
          <span className="text-xs text-muted-foreground">
            {t.dialogs.agreementAcceptance.scrollToAccept}
          </span>
          <ChevronDown className="h-4 w-4 text-muted-foreground animate-bounce" />
        </div>
      )}
    </div>
  );
}
